import React, { useState } from 'react';

const RestaurantForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    restaurantName: initialData?.restaurantName || '',
    restaurantAddress: initialData?.restaurantAddress || '',
    date: initialData?.date || '',
    time: initialData?.time || '',
    nameOnReservation: initialData?.nameOnReservation || '',
    partySize: initialData?.partySize || '',
    confirmationNumber: initialData?.confirmationNumber || '',
    phoneNumber: initialData?.phoneNumber || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.price) || 0,
      partySize: parseInt(formData.partySize) || 1
    });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      url: URL.createObjectURL(file),
      file
    }));
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos]
    }));
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    const newDocs = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      url: URL.createObjectURL(file),
      file
    }));
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocs]
    }));
  };

  const removePhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const removeDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.id !== docId)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="component-form restaurant-form">
      <div className="form-section">
        <h4>Restaurant Information</h4>
        
        <div className="form-group">
          <label>Title/Description*</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., Dinner at Le Bernardin"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Restaurant Name*</label>
            <input
              type="text"
              value={formData.restaurantName}
              onChange={(e) => setFormData({...formData, restaurantName: e.target.value})}
              placeholder="e.g., Le Bernardin"
              required
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
              placeholder="e.g., (555) 123-4567"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Restaurant Address*</label>
          <input
            type="text"
            value={formData.restaurantAddress}
            onChange={(e) => setFormData({...formData, restaurantAddress: e.target.value})}
            placeholder="e.g., 155 West 51st Street, New York, NY 10019"
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Reservation Details</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Date*</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Time*</label>
            <input
              type="time"
              value={formData.time}
              onChange={(e) => setFormData({...formData, time: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Name on Reservation*</label>
            <input
              type="text"
              value={formData.nameOnReservation}
              onChange={(e) => setFormData({...formData, nameOnReservation: e.target.value})}
              placeholder="e.g., John Smith"
              required
            />
          </div>
          <div className="form-group">
            <label>Party Size*</label>
            <input
              type="number"
              value={formData.partySize}
              onChange={(e) => setFormData({...formData, partySize: e.target.value})}
              placeholder="Number of guests"
              min="1"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Confirmation Number</label>
            <input
              type="text"
              value={formData.confirmationNumber}
              onChange={(e) => setFormData({...formData, confirmationNumber: e.target.value})}
              placeholder="e.g., RES123456"
            />
          </div>
          <div className="form-group">
            <label>Price per Person*</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Additional Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Special requests, dietary restrictions, occasion, etc..."
            rows="3"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Attachments</h4>
        
        <div className="form-group">
          <label>Photos</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            className="file-input"
          />
          {formData.photos.length > 0 && (
            <div className="file-list">
              {formData.photos.map(photo => (
                <div key={photo.id} className="file-item">
                  <span>{photo.name}</span>
                  <button type="button" onClick={() => removePhoto(photo.id)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Documents</label>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx"
            onChange={handleDocumentUpload}
            className="file-input"
          />
          {formData.documents.length > 0 && (
            <div className="file-list">
              {formData.documents.map(doc => (
                <div key={doc.id} className="file-item">
                  <span>{doc.name}</span>
                  <button type="button" onClick={() => removeDocument(doc.id)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Add'} Restaurant
        </button>
      </div>
    </form>
  );
};

export default RestaurantForm;
